import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../../common/resources/SoundsResourcePackageWrapper";
import { IRaidController } from "../controllers/IRaidController";
import { RaidResourcePackage } from "../resources/RaidResourcePackage";
import { CardTargetsPage } from "./pages/CardTargetsPage";
import { CardsPage } from "./pages/CardsPage";
import { SummaryPage } from "./pages/SummaryPage";
import { ShieldPage } from "./pages/ShieldPage";
import { DiceRollRaidGameResultType } from "../../../game/flow/impl/diceActions/raidAction/DiceRollRaidGameResultType";
import { TopPanel } from "../../../game/components/toppanel/TopPanel";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { RaidTutorialResourcePackage } from "../resources/RaidTutorialResourcePackage";
import { UserProfileResourcePackage } from "../../userprofile/resources/UserProfileResourcePackage";
import { RaidTargetsData } from "../../../game/flow/impl/diceActions/raidAction/RaidTargetsData";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { NoCacheDynamicPackage } from "../../../common/resources/NoCacheDynamicPackage";
import { RaidAction } from "../../../game/flow/impl/diceActions/raidAction/RaidAction";

export class RaidPopupView extends BaseScalableView<void> {
    private readonly commonResources: CommonResourcesPackage;
    private readonly raidResources: RaidResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly raidController: IRaidController;
    private readonly topPanel: TopPanel;
    private readonly isFirstShow: boolean;
    private readonly tutorialResources: RaidTutorialResourcePackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly raidDynamicResources: NoCacheDynamicPackage;
    private readonly action: RaidAction;

    public constructor(
        commonResources: CommonResourcesPackage,
        raidResources: RaidResourcePackage,
        tutorialResources: RaidTutorialResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        userProfileResources: UserProfileResourcePackage,
        raidController: IRaidController,
        canvasScaler: ICanvasScaler,
        isFirstShow: boolean,
        raidDynamicResources: NoCacheDynamicPackage,
        action: RaidAction,
        operationControls: PromiseCompletionSource<void>,
        topPanel: TopPanel,
        asyncOperationNotification: AsyncOperationNotification
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.soundResources = soundResources;
        this.raidResources = raidResources;
        this.tutorialResources = tutorialResources;
        this.userProfileResources = userProfileResources;
        this.raidController = raidController;
        this.isFirstShow = isFirstShow;
        this.raidDynamicResources = raidDynamicResources;
        this.action = action;
        this.topPanel = topPanel;
        this.asyncOperationNotification = asyncOperationNotification;
    }

    public async start(): Promise<void> {
        const cardsPageCS = new PromiseCompletionSource<void>();
        const cardsPage = new CardsPage(
            this.raidResources,
            this.commonResources,
            this.soundResources,
            this.raidController,
            this.userProfileResources,
            this.canvasScaler,
            this.isFirstShow,
            this.raidDynamicResources,
            this.action,
            cardsPageCS
        );
        const cardTargetsPageCS = new PromiseCompletionSource<RaidTargetsData>();
        this.addChild(cardsPage);
        await cardsPageCS.result;
        const cardTargetsPage = new CardTargetsPage(
            this.commonResources,
            this.raidResources,
            this.soundResources,
            this.raidController,
            this.canvasScaler,
            this.isFirstShow,
            this.asyncOperationNotification,
            cardTargetsPageCS
        );
        const summaryPageCS = new PromiseCompletionSource<void>();
        this.addChild(cardTargetsPage);
        const raidTargetsData = await cardTargetsPageCS.result;
        if (raidTargetsData.diceRollRaidGameResult == DiceRollRaidGameResultType.COMPENSATION_WIN) {
            const shieldPageCS = new PromiseCompletionSource<void>();
            const shieldPage = new ShieldPage(
                this.canvasScaler,
                this.raidResources,
                this.soundResources,
                shieldPageCS
            );
            this.addChild(shieldPage);
            await shieldPageCS.result;
            this.removeChild(shieldPage);
        }
        const raidSummaryPage = new SummaryPage(
            this.commonResources,
            this.raidResources,
            this.soundResources,
            this.raidController,
            this.canvasScaler,
            this.isFirstShow,
            this.tutorialResources,
            summaryPageCS,
            raidTargetsData,
            this.topPanel
        );
        this.addChild(raidSummaryPage);
        this.removeChild(cardsPage);
        this.removeChild(cardTargetsPage);
        await summaryPageCS.result;
        this.removeChild(raidSummaryPage);
        this.operationControls.complete();
    }
}
